import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import {
  ExploreRegionQueryResult,
  useGetBucketListItemsIndexQuery,
} from '../../../hooks/useQueries'
import { media } from '../../../styles/media'

import { InsetContainer } from '../../../components/layout/Container'
import ButtonRouterLink from '../../../components/display/ButtonRouterLink'
import { HorizontalListTrack } from '../../../components/display/HorizontalList'
import { Grid, GridItem } from '../../../components/layout/ItemGrid'
import StoryCard from '../../../components/cards/StoryCard'
import WithSelectEscape from '../../../components/SelectEscape/WithSelectEscape'

import Adventures from './Adventures'

const AdventuresWrapper = styled.section`
  margin-top: var(--ds-space-07);
`

const StoryListWrapper = styled.div`
  ${media.md`
    width: 100%;
    max-width: 1300px;
    margin: 0 auto;
    padding: 0 var(--ds-space-05);
  `}
`

/* Adventure cards end in 32px of internal padding (unlike the image-only
   collection tiles), so a small offset lands the same 40px visual gap as
   All Collections. */
const AllRegion = styled.div`
  margin-top: var(--ds-space-02);
  text-align: center;
`

type ExploreRegionNode = ExploreRegionQueryResult['data']['explore']['region']

type ExploreSectionProps = ExploreRegionNode & {
  countryCode: GQL_Countries
  state: {
    code: string
  }
}

const ExploreSection: FunctionComponent<ExploreSectionProps> = ({
  id,
  countryCode,
  state,
  slug,
  name,
  stories,
}) => {
  const { data: bucketListIndexData } = useGetBucketListItemsIndexQuery({
    fetchPolicy: 'cache-first',
  })
  // SSR-safe (false on the server + first client render, then the real value),
  // so the story grid renders server-side instead of being gated behind <Media>.
  const { lg: isLg } = useMediaQueryContext()

  const bucketListItemsIndex = bucketListIndexData?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex

  return (
    <div>
      <StoryListWrapper>
        <HorizontalListTrack>
          <Grid columns={{ xxxl: 3 }}>
            {stories.map((story) => (
              <GridItem key={story.id}>
                <WithSelectEscape
                  itemId={story.id}
                  itemType="Story"
                  bucketListItemsIndex={bucketListItemsIndex}
                >
                  <StoryCard
                    story={story}
                    listing={story.featuredListing}
                    sameTab={!isLg}
                  />
                </WithSelectEscape>
              </GridItem>
            ))}
          </Grid>
        </HorizontalListTrack>
      </StoryListWrapper>

      <AdventuresWrapper>
        <Adventures regionId={id} />
      </AdventuresWrapper>

      <InsetContainer>
        <AllRegion>
          <ButtonRouterLink
            to={`/listings?type=accommodation&country=${countryCode}&state=${state.code}&regions[]=${slug}`}
            ghost
            inline
          >
            All {name}
          </ButtonRouterLink>
        </AllRegion>
      </InsetContainer>
    </div>
  )
}

export default ExploreSection
